import { mainRoutes, routes } from '@constants/routes.const'
import { useEffect, useCallback } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useTypedSelector } from 'store'

interface HistoryEntry {
  path: string
  timestamp: string
}

const useNavigationHistory = () => {
  const location = useLocation()
  const navigate = useNavigate()
  const initialRoute = useTypedSelector((state) => state.menu.initialRoute)

  // Move the history update logic to a separate function
  const updateHistory = useCallback(
    (currentPath: string) => {
      const isPlayerRoute = currentPath.includes(routes.PLAYER)
      const isHomePage = currentPath === initialRoute || currentPath.includes(mainRoutes.HOME)

      if (isPlayerRoute) {
        navigate(currentPath, { replace: true })
        return
      }

      const storedHistory: HistoryEntry[] = JSON.parse(sessionStorage.getItem('navigationHistory') || '[]')
      let newHistory = [...storedHistory]

      if (isHomePage) {
        newHistory = [
          {
            path: currentPath,
            timestamp: new Date().toISOString(),
          },
        ]
      } else {
        const lastEntry = newHistory[newHistory.length - 1]
        if (!lastEntry || lastEntry.path !== currentPath) {
          newHistory.push({
            path: currentPath,
            timestamp: new Date().toISOString(),
          })
        }
      }

      // Keep only unique paths by removing older duplicates
      const seen = new Set<string>()
      newHistory = newHistory
        .reverse()
        .filter((entry) => {
          if (seen.has(entry.path)) {
            return false
          }
          seen.add(entry.path)
          return true
        })
        .reverse()

      sessionStorage.setItem('navigationHistory', JSON.stringify(newHistory))
    },
    [initialRoute, navigate],
  )

  useEffect(() => {
    updateHistory(location.pathname)
  }, [location.pathname, updateHistory])

  const safeGoBack = useCallback(
    (fallbackRoute = initialRoute) => {
      const storedHistory: HistoryEntry[] = JSON.parse(sessionStorage.getItem('navigationHistory') || '[]')

      const filteredHistory = storedHistory.filter((entry) => entry.path !== location.pathname)
      sessionStorage.setItem('navigationHistory', JSON.stringify(filteredHistory))

      const previousPath = filteredHistory[filteredHistory.length - 1]?.path
      navigate(previousPath ?? fallbackRoute ?? '/')
    },
    [initialRoute, location.pathname, navigate],
  )

  return { safeGoBack }
}

export default useNavigationHistory
